import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import { Person } from '../../../models/Entities/Person/Person';
import {
  PersonRelationshipCreateCommand,
  PersonRelationshipUpdateCommand
} from '../../../models/Entities/Person/Relationship/PersonRelationshipModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import RelationshipTypeSelectorComponent from '../Selectors/RelationshipTypeSelector/RelationshipTypeSelector.Component';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ownFunctions from './PersonRelationship.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonRelationshipFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [relatedPersonId, setRelatedPersonId] = useState<number | undefined>(undefined);
  const [relationshipTypeId, setRelationshipTypeId] = useState<number | undefined>(undefined);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    props.GetAllPersons();
  }, []);

  useEffect(() => {
    if (props.relationship) {
      setRelatedPersonId(props.relationship.relatedPerson?.id);
      setRelationshipTypeId(props.relationship.relationshipType?.id);
      setIsActive(props.relationship.isActive ?? true);
      setStartDate(CommonFunctions.ToInputDate(props.relationship.startDate));
      setEndDate(CommonFunctions.ToInputDate(props.relationship.endDate));
    } else {
      resetForm();
    }
  }, [props.relationship, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setRelatedPersonId(undefined);
    setRelationshipTypeId(undefined);
    setIsActive(true);
    setStartDate('');
    setEndDate('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!relatedPersonId) {
      errors.push(
        t('persons.relationships.form.validation.relatedPerson', {
          defaultValue: 'La persona relacionada es obligatoria'
        })
      );
    }

    if (!relationshipTypeId) {
      errors.push(
        t('persons.relationships.form.validation.relationshipType', {
          defaultValue: 'El tipo de relación es obligatorio'
        })
      );
    }

    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      errors.push(
        t('persons.relationships.form.validation.dates', {
          defaultValue: 'La fecha de fin debe ser posterior a la de inicio'
        })
      );
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: PersonRelationshipCreateCommand = {
      personId: props.PersonId,
      relatedPersonId: relatedPersonId!,
      relationshipTypeId: relationshipTypeId!,
      isActive,
      startDate: CommonFunctions.FromInputDate(startDate),
      endDate: CommonFunctions.FromInputDate(endDate)
    };

    if (props.relationship) {
      props.UpdatePersonRelationship({
        ...payload,
        id: props.relationship.id
      } as PersonRelationshipUpdateCommand);
    } else {
      props.CreatePersonRelationship(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const selectedRelatedPerson = props.availablePersons.find(
    (person: Person) => person.id === relatedPersonId
  );

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.relationship
            ? t('persons.relationships.form.editTitle', { defaultValue: 'Editar relación' })
            : t('persons.relationships.form.addTitle', { defaultValue: 'Nueva relación' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label={t('persons.relationships.form.relatedPerson', { defaultValue: 'Persona relacionada' })}
            required
            validationMessage={
              submitted && !relatedPersonId
                ? t('persons.relationships.form.validation.relatedPerson', {
                    defaultValue: 'La persona relacionada es obligatoria'
                  })
                : undefined
            }
            validationState={submitted && !relatedPersonId ? 'error' : undefined}
          >
            <Dropdown
              placeholder={t('persons.relationships.form.relatedPersonPlaceholder', {
                defaultValue: 'Seleccione una persona'
              })}
              value={CommonFunctions.GetPersonDisplayName(selectedRelatedPerson)}
              selectedOptions={relatedPersonId ? [String(relatedPersonId)] : []}
              onOptionSelect={(_, data) =>
                setRelatedPersonId(data.optionValue ? Number(data.optionValue) : undefined)
              }
            >
              {props.availablePersons.map((person: Person) => (
                <Option
                  key={person.id}
                  value={String(person.id)}
                  text={CommonFunctions.GetPersonDisplayName(person)}
                >
                  {CommonFunctions.GetPersonDisplayName(person)}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <RelationshipTypeSelectorComponent
            selectedRelationshipTypeId={relationshipTypeId}
            personType={props.PersonType}
            isRequired
            errorMessage={
              submitted && !relationshipTypeId
                ? t('persons.relationships.form.validation.relationshipType', {
                    defaultValue: 'El tipo de relación es obligatorio'
                  })
                : undefined
            }
            onSelectionChange={(value: number | undefined) => setRelationshipTypeId(value)}
          />

          <Field label={t('persons.relationships.form.startDate', { defaultValue: 'Fecha de inicio' })}>
            <Input type="date" value={startDate} onChange={(_, data) => setStartDate(data.value || '')} />
          </Field>

          <Field label={t('persons.relationships.form.endDate', { defaultValue: 'Fecha de fin' })}>
            <Input type="date" value={endDate} onChange={(_, data) => setEndDate(data.value || '')} />
          </Field>

          <Checkbox
            label={t('persons.relationships.form.isActive', { defaultValue: 'Relación activa' })}
            checked={isActive}
            onChange={(_, data) => setIsActive(!!data.checked)}
          />
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonRelationshipFormComponent as any);
